import { useEffect } from "react";
import useContent from "@/lib/useContent";

/**
 * Lightweight SEO head manager — sets <title>, meta description,
 * canonical, Open Graph and Twitter tags directly on document.head
 * without pulling in react-helmet-async. Mount it at the top of any page.
 *
 * Pass `cmsPage` to layer CMS overrides on top of the provided defaults.
 *
 * Best practice: keep title <= 60 chars, description <= 160 chars.
 */
export default function SEOHead(props) {
  if (props.cmsPage) {
    return <CmsAwareSEOHead {...props} />;
  }
  return <RawSEOHead {...props} />;
}

function CmsAwareSEOHead({ cmsPage, title, description, ...rest }) {
  const { get } = useContent(cmsPage);
  return (
    <RawSEOHead
      title={get("seo.title", title)}
      description={get("seo.description", description)}
      {...rest}
    />
  );
}

function RawSEOHead({
  title,
  description,
  canonical,
  image,
  type = "website",
  jsonLd = null,
  noIndex = false,
}) {
  useEffect(() => {
    if (typeof document === "undefined") return;
    const prev = document.title;

    if (title) document.title = title;

    const tags = [
      { name: "description", content: description },
      { name: "robots", content: noIndex ? "noindex,nofollow" : "index,follow" },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: type },
      { property: "og:image", content: image },
      { property: "og:site_name", content: "Temple of Kulture" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
      { name: "twitter:image", content: image },
      { name: "theme-color", content: "#050505" },
    ].filter((t) => t.content);

    const created = [];
    tags.forEach(({ name, property, content }) => {
      const key = name || property;
      const selector = name
        ? `meta[name="${name}"]`
        : `meta[property="${property}"]`;
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        if (name) el.setAttribute("name", name);
        if (property) el.setAttribute("property", property);
        document.head.appendChild(el);
        created.push({ el, key });
      }
      el.setAttribute("content", content);
    });

    // Canonical
    let canonicalEl = document.head.querySelector('link[rel="canonical"]');
    const fullCanonical =
      canonical ||
      (typeof window !== "undefined"
        ? `${window.location.origin}${window.location.pathname}`
        : null);
    if (fullCanonical) {
      if (!canonicalEl) {
        canonicalEl = document.createElement("link");
        canonicalEl.setAttribute("rel", "canonical");
        document.head.appendChild(canonicalEl);
        created.push({ el: canonicalEl, key: "canonical" });
      }
      canonicalEl.setAttribute("href", fullCanonical);
    }

    // JSON-LD (e.g., Event, EducationalOrganization, Service)
    let jsonLdEl = null;
    if (jsonLd) {
      jsonLdEl = document.createElement("script");
      jsonLdEl.setAttribute("type", "application/ld+json");
      jsonLdEl.textContent = JSON.stringify(jsonLd);
      jsonLdEl.dataset.seoHead = "1";
      document.head.appendChild(jsonLdEl);
    }

    return () => {
      document.title = prev;
      if (jsonLdEl?.parentNode) jsonLdEl.parentNode.removeChild(jsonLdEl);
      // We intentionally leave meta tags in place — next SEOHead overwrites
      // their content. Only nodes we created get cleaned on full unmount.
    };
  }, [title, description, canonical, image, type, jsonLd, noIndex]);

  return null;
}
